import { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { useTranslation } from 'react-i18next'
import { useGroupStore } from '@/stores/groupStore'
import {
  Box,
  Typography,
  Alert,
  Card,
  CardContent,
  Skeleton,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  Button,
  Breadcrumbs,
  Divider,
  FormControl,
  InputLabel,
  Select,
  TextField,
  Chip,
} from '@mui/material'
import Link from '@mui/material/Link'
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  PersonAdd as PersonAddIcon,
  CalendarMonth as CalendarMonthIcon,
  MoreVert as MoreVertIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
  ChatBubbleOutline as ChatIcon,
} from '@mui/icons-material'
import { Link as RouterLink } from 'react-router-dom'
import { GroupMembers } from '@/components/groups/GroupMembers'
import { GroupFinanceSection } from '@/components/billing/GroupFinanceSection'
import { GroupForm } from '@/components/groups/GroupForm'
import { AddMemberModal } from '@/components/groups/AddMemberModal'
import { GroupStatusBadge } from '@/components/groups/GroupStatusBadge'
import { ROUTES } from '@/utils/constants'
import type { UpdateGroupData, AddMemberData } from '@/types'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import '@/styles/dashboard.css'
import { lessonSchedulesApi } from '@/api/lessonSchedules'
import { validateSchedulePauses } from '@/components/lessonSchedule/schedulePauseValidation'
import { formatDateDMY } from '@/utils/date'
import type { LessonSchedulePause, LessonScheduleSlot, LessonScheduleTemplate } from '@/types'

export function GroupDetailsPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const {
    currentGroup,
    isLoading,
    error,
    fetchGroup,
    updateGroup,
    deleteGroup,
    removeMember,
    addMember,
    archiveGroup,
    restoreGroup,
    clearError,
  } = useGroupStore()

  const groupId = id ? parseInt(id, 10) : null

  const [groupSchedule, setGroupSchedule] = useState<LessonScheduleTemplate | null>(null)
  const [scheduleLoading, setScheduleLoading] = useState(false)
  const [scheduleError, setScheduleError] = useState<string | null>(null)

  const [scheduleEditOpen, setScheduleEditOpen] = useState(false)
  const [editorSlots, setEditorSlots] = useState<LessonScheduleSlot[]>([])
  const [editorPauses, setEditorPauses] = useState<LessonSchedulePause[]>([])
  const [scheduleEditorError, setScheduleEditorError] = useState<string | null>(null)

  const weekdayLabels: Record<number, string> = useMemo(
    () => ({
      1: 'Понедельник',
      2: 'Вторник',
      3: 'Среда',
      4: 'Четверг',
      5: 'Пятница',
      6: 'Суббота',
      7: 'Воскресенье',
    }),
    []
  )

  const fetchGroupSchedule = async () => {
    if (!groupId) return
    setScheduleLoading(true)
    setScheduleError(null)
    try {
      const res = await lessonSchedulesApi.getGroupSchedule(groupId)
      setGroupSchedule(res)
    } catch (e: unknown) {
      const err = e as { message?: string }
      setScheduleError(err.message || 'Ошибка загрузки расписания')
    } finally {
      setScheduleLoading(false)
    }
  }

  useEffect(() => {
    fetchGroupSchedule().catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId])

  const openScheduleEditor = () => {
    setScheduleEditorError(null)
    if (groupSchedule) {
      setEditorSlots((groupSchedule.slots ?? []).map((s) => ({ ...s })))
      setEditorPauses((groupSchedule.pauses ?? []).map((p) => ({ ...p })))
    } else {
      setEditorSlots([])
      setEditorPauses([])
    }
    setScheduleEditOpen(true)
  }

  const handleSaveGroupSchedule = async () => {
    if (!groupId) return
    setScheduleEditorError(null)

    const pausesErrorKey = validateSchedulePauses(editorPauses)
    if (pausesErrorKey) {
      setScheduleEditorError(t(pausesErrorKey))
      return
    }

    try {
      const upserted = await lessonSchedulesApi.upsertGroupSchedule(groupId, {
        is_active: groupSchedule?.is_active ?? true,
      })

      const slotsPayload = editorSlots.map((s) => ({
        weekday: s.weekday,
        start_time: s.start_time.includes(':') ? s.start_time : `${s.start_time}:00`,
        end_time: s.end_time.includes(':') ? s.end_time : `${s.end_time}:00`,
      }))
      await lessonSchedulesApi.updateGroupSlots(groupId, upserted.id, slotsPayload)

      const existingPauseIds = new Set((groupSchedule?.pauses ?? []).map((p) => p.id))
      await lessonSchedulesApi.updateGroupPauses(
        groupId,
        upserted.id,
        editorPauses.map((p) => ({
          id: existingPauseIds.has(p.id) ? p.id : undefined,
          date_from: p.date_from,
          date_to: p.date_to,
          reason: p.reason ?? null,
        }))
      )

      setScheduleEditOpen(false)
      await fetchGroupSchedule()
    } catch (e: unknown) {
      const err = e as { message?: string }
      setScheduleEditorError(err.message || 'Ошибка сохранения расписания')
    }
  }

  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false)
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)

  useEffect(() => {
    if (id) {
      fetchGroup(parseInt(id))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const handleEdit = () => {
    setMenuAnchor(null)
    setIsEditModalOpen(true)
  }

  const handleDelete = async () => {
    setMenuAnchor(null)
    if (!currentGroup) return
    if (window.confirm(t('groups.deleteConfirm'))) {
      try {
        await deleteGroup(currentGroup.id)
        navigate(ROUTES.GROUPS)
      } catch (err) {
        // Error handled in store
      }
    }
  }

  const handleAddMember = () => {
    setMenuAnchor(null)
    setIsAddMemberModalOpen(true)
  }

  const handleEditGroup = async (data: UpdateGroupData) => {
    if (!currentGroup) return
    try {
      await updateGroup(currentGroup.id, data)
      setIsEditModalOpen(false)
      fetchGroup(currentGroup.id)
    } catch (err) {
      // Error handled in store
    }
  }

  const handleRemoveMember = async (studentId: number) => {
    if (!currentGroup) return
    try {
      await removeMember(currentGroup.id, studentId)
      fetchGroup(currentGroup.id)
    } catch (err) {
      // Error handled in store
    }
  }

  const handleAddMemberSubmit = async (data: AddMemberData) => {
    if (!currentGroup) return
    try {
      await addMember(currentGroup.id, data)
      setIsAddMemberModalOpen(false)
      fetchGroup(currentGroup.id)
    } catch (err) {
      // Error handled in store
    }
  }

  const handleArchive = async () => {
    setMenuAnchor(null)
    if (!currentGroup) return
    if (window.confirm(t('groups.archiveConfirm'))) {
      try {
        await archiveGroup(currentGroup.id)
        fetchGroup(currentGroup.id)
      } catch (err) {
        // Error handled in store
      }
    }
  }

  const handleRestore = async () => {
    setMenuAnchor(null)
    if (!currentGroup) return
    if (window.confirm(t('groups.restoreConfirm'))) {
      try {
        await restoreGroup(currentGroup.id)
        fetchGroup(currentGroup.id)
      } catch (err) {
        // Error handled in store
      }
    }
  }

  const showSkeleton = isLoading || !currentGroup

  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link component={RouterLink} to={ROUTES.DASHBOARD} underline="hover" sx={{ display: 'flex', alignItems: 'center' }}>
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('dashboard.home')}
          </Link>
          <Link component={RouterLink} to={ROUTES.GROUPS} underline="hover">
            {t('dashboard.groups')}
          </Link>
          {showSkeleton ? (
            <Skeleton variant="text" width={120} height={24} />
          ) : (
            <Typography color="text.primary">{currentGroup?.name ?? '—'}</Typography>
          )}
        </Breadcrumbs>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 2,
            mb: 3,
          }}
        >
          <IconButton onClick={() => navigate(ROUTES.GROUPS)}>
            <ArrowBackIcon />
          </IconButton>
          {showSkeleton ? (
            <Skeleton variant="text" width={200} height={40} sx={{ flexGrow: 1, maxWidth: 320 }} />
          ) : (
            <Typography
              variant="h4"
              component="h1"
              fontWeight={700}
              sx={{
                flexGrow: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: { xs: 'nowrap', sm: 'normal' },
              }}
            >
              {currentGroup!.name}
            </Typography>
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              ml: 'auto',
              width: { xs: '100%', sm: 'auto' },
              justifyContent: { xs: 'flex-end', sm: 'flex-start' },
            }}
          >
            {showSkeleton ? (
              <>
                <Skeleton variant="rectangular" width={90} height={24} sx={{ borderRadius: 1 }} />
                <Skeleton variant="circular" width={40} height={40} />
              </>
            ) : (
              <>
                <PageHelpPopover pageKey="groups" />
                <GroupStatusBadge status={currentGroup!.status} label={currentGroup!.status_label} />
                <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)}>
                  <MoreVertIcon />
                </IconButton>
              </>
            )}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
            {error}
          </Alert>
        )}

        {showSkeleton ? (
          <>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Skeleton variant="text" width="30%" height={28} sx={{ mb: 2 }} />
                <Skeleton variant="text" width="90%" height={24} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="60%" height={24} />
                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  <Skeleton variant="text" width={100} height={20} />
                  <Skeleton variant="text" width={80} height={20} />
                </Box>
              </CardContent>
            </Card>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                <Skeleton variant="text" width={200} height={28} />
                <Skeleton variant="rectangular" width={190} height={40} sx={{ borderRadius: 1 }} />
              </Box>
              <Skeleton variant="rectangular" width="100%" height={100} sx={{ borderRadius: 1 }} />
            </Box>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Skeleton variant="text" width={120} height={28} />
                <Skeleton variant="rectangular" width={190} height={40} sx={{ borderRadius: 1 }} />
              </Box>
              {[1, 2, 3].map((i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Skeleton variant="circular" width={40} height={40} />
                  <Skeleton variant="text" width="60%" height={24} />
                  <Skeleton variant="text" width={80} height={20} sx={{ ml: 'auto' }} />
                </Box>
              ))}
            </Box>
          </>
        ) : (
          <>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('groups.description')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {currentGroup.description || t('groups.noDescription')}
            </Typography>
            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {t('groups.createdAt')}
                </Typography>
                <Typography variant="body1">
                  {new Date(currentGroup.created_at).toLocaleDateString('ru-RU')}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {t('groups.membersCount')}
                </Typography>
                <Typography variant="body1">
                  {currentGroup.members_count}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Расписание группы — заголовок и кнопка как у блока «Участники» */}
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              mb: 2,
            }}
          >
            <Typography variant="h6" component="h2">
              {t('lessonSchedules.groupTitle', 'Расписание группы')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<CalendarMonthIcon />}
              onClick={openScheduleEditor}
              disabled={scheduleLoading}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              {t('lessonSchedules.editSchedule', 'Изменить расписание')}
            </Button>
          </Box>

          {scheduleError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setScheduleError(null)}>
              {scheduleError}
            </Alert>
          )}

          <Card variant="outlined">
            <CardContent>
              {scheduleLoading ? (
                <Skeleton variant="rectangular" width="100%" height={120} sx={{ borderRadius: 1 }} />
              ) : groupSchedule ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {[1, 2, 3, 4, 5, 6, 7].map((weekday) => {
                    const daySlots = (groupSchedule.slots ?? [])
                      .filter((s) => s.weekday === weekday)
                      .sort((a, b) => a.start_time.localeCompare(b.start_time))

                    if (daySlots.length === 0) return null
                    return (
                      <Box key={weekday} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography color="text.secondary" variant="body2" sx={{ minWidth: 160 }}>
                          {weekdayLabels[weekday]}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {daySlots.map((s) => (
                            <Chip
                              key={`${s.id}-${s.start_time}`}
                              size="medium"
                              label={`${s.start_time.slice(0, 5)}-${s.end_time.slice(0, 5)}`}
                              sx={{
                                '& .MuiChip-label': {
                                  fontSize: '1rem',
                                  fontWeight: 600,
                                  py: 0.25,
                                },
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )
                  })}
                  {(groupSchedule.slots ?? []).length === 0 && (
                    <Typography color="text.secondary">{t('common.none', '—')}</Typography>
                  )}

                  <Divider sx={{ my: 1 }} />

                  <Typography variant="subtitle2" fontWeight={700}>
                    {t('lessons.pausesTitle', 'Паузы / отпуска')}
                  </Typography>
                  {(groupSchedule.pauses ?? []).length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {(groupSchedule.pauses ?? []).map((p) => (
                        <Box
                          key={p.id}
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            py: 0.5,
                          }}
                        >
                          <Typography variant="body2" fontWeight={600}>
                            {formatDateDMY(p.date_from)} – {formatDateDMY(p.date_to)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                            {p.reason?.trim() ? p.reason : '—'}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography color="text.secondary" variant="body2">
                      {t('common.none', '—')}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography color="text.secondary" variant="body2">
                  {t('lessonSchedules.empty', 'Расписание не задано')}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              mb: 2,
            }}
          >
            <Typography variant="h6" component="h2">
              {t('groups.members')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setIsAddMemberModalOpen(true)}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              {t('groups.addMember')}
            </Button>
          </Box>
          <GroupMembers
            members={currentGroup.members || []}
            onRemoveMember={handleRemoveMember}
            isLoading={isLoading}
            isTeacher={true}
          />
        </Box>

        {currentGroup && <GroupFinanceSection groupId={currentGroup.id} />}

        {/* Диалог редактирования расписания группы */}
        <Dialog open={scheduleEditOpen} onClose={() => setScheduleEditOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>{t('lessonSchedules.editScheduleTitle', 'Редактирование расписания')}</DialogTitle>
          <DialogContent>
            {scheduleEditorError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {scheduleEditorError}
              </Alert>
            )}

            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              {t('lessons.slotsTitle', 'Слоты')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
              {editorSlots.map((s, idx) => (
                <Box
                  key={`${s.id}-${idx}`}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '120px 180px 180px 1fr 40px' },
                    gap: 1,
                    alignItems: 'center',
                  }}
                >
                  <FormControl size="small">
                    <InputLabel>{t('lessons.weekday', 'День недели')}</InputLabel>
                    <Select
                      label={t('lessons.weekday', 'День недели')}
                      value={s.weekday}
                      onChange={(e) => {
                        const next = [...editorSlots]
                        next[idx] = { ...s, weekday: Number(e.target.value) }
                        setEditorSlots(next)
                      }}
                    >
                      {[1, 2, 3, 4, 5, 6, 7].map((w) => (
                        <MenuItem key={w} value={w}>
                          {weekdayLabels[w]}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    size="small"
                    type="time"
                    value={s.start_time ? s.start_time.slice(0, 5) : '00:00'}
                    label={t('lessons.startTime', 'Начало')}
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => {
                      const next = [...editorSlots]
                      next[idx] = { ...s, start_time: `${e.target.value}:00` }
                      setEditorSlots(next)
                    }}
                  />
                  <TextField
                    size="small"
                    type="time"
                    value={s.end_time ? s.end_time.slice(0, 5) : '00:00'}
                    label={t('lessons.endTime', 'Конец')}
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => {
                      const next = [...editorSlots]
                      next[idx] = { ...s, end_time: `${e.target.value}:00` }
                      setEditorSlots(next)
                    }}
                  />

                  <IconButton onClick={() => setEditorSlots(editorSlots.filter((_, i) => i !== idx))}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}

              <Button
                startIcon={<AddIcon />}
                size="small"
                variant="outlined"
                onClick={() => {
                  setEditorSlots((prev) => [
                    ...prev,
                    {
                      id: Date.now(),
                      weekday: 1,
                      start_time: '09:00:00',
                      end_time: '10:00:00',
                    },
                  ])
                }}
              >
                {t('lessons.addSlot', 'Добавить слот')}
              </Button>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              {t('lessons.pausesTitle', 'Паузы / отпуска')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {editorPauses.map((p, idx) => (
                <Box
                  key={`${p.id}-${idx}`}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '160px 160px 1fr 40px' },
                    gap: 1,
                    alignItems: 'center',
                  }}
                >
                  <TextField
                    size="small"
                    type="date"
                    value={p.date_from}
                    label={t('lessons.dateFrom', 'С')}
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => {
                      const next = [...editorPauses]
                      next[idx] = { ...p, date_from: e.target.value }
                      setEditorPauses(next)
                    }}
                  />
                  <TextField
                    size="small"
                    type="date"
                    value={p.date_to}
                    label={t('lessons.dateTo', 'По')}
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => {
                      const next = [...editorPauses]
                      next[idx] = { ...p, date_to: e.target.value }
                      setEditorPauses(next)
                    }}
                  />
                  <TextField
                    size="small"
                    value={p.reason ?? ''}
                    label={t('lessons.pauseReason', 'Причина')}
                    onChange={(e) => {
                      const next = [...editorPauses]
                      next[idx] = { ...p, reason: e.target.value || null }
                      setEditorPauses(next)
                    }}
                  />
                  <IconButton onClick={() => setEditorPauses(editorPauses.filter((_, i) => i !== idx))}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}

              <Button
                startIcon={<AddIcon />}
                size="small"
                variant="outlined"
                onClick={() => {
                  const today = new Date().toISOString().slice(0, 10)
                  setEditorPauses((prev) => [
                    ...prev,
                    { id: Date.now(), date_from: today, date_to: today, reason: null },
                  ])
                }}
              >
                {t('lessons.addPause', 'Добавить паузу')}
              </Button>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setScheduleEditOpen(false)}>{t('common.cancel', 'Отмена')}</Button>
            <Button variant="contained" onClick={handleSaveGroupSchedule} disabled={!groupId}>
              {t('common.save', 'Сохранить')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Меню действий */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
        >
          <MenuItem onClick={handleEdit}>
            <EditIcon sx={{ mr: 1 }} fontSize="small" />
            {t('common.edit')}
          </MenuItem>
          <MenuItem onClick={handleAddMember}>
            <PersonAddIcon sx={{ mr: 1 }} fontSize="small" />
            {t('groups.addMember')}
          </MenuItem>
          {groupId && (
            <MenuItem
              onClick={() => {
                setMenuAnchor(null)
                navigate(ROUTES.CHAT_GROUP(groupId))
              }}
            >
              <ChatIcon sx={{ mr: 1 }} fontSize="small" />
              {t('chat.groupChat')}
            </MenuItem>
          )}
          {currentGroup.status === 'active' ? (
            <MenuItem onClick={handleArchive}>
              <ArchiveIcon sx={{ mr: 1 }} fontSize="small" />
              {t('groups.archive')}
            </MenuItem>
          ) : (
            <MenuItem onClick={handleRestore}>
              <UnarchiveIcon sx={{ mr: 1 }} fontSize="small" />
              {t('groups.restore')}
            </MenuItem>
          )}
          <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
            {t('common.delete')}
          </MenuItem>
        </Menu>

        {/* Модальное окно редактирования */}
        <Dialog
          open={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t('groups.edit')}</DialogTitle>
          <DialogContent>
            <GroupForm
              initialValues={currentGroup}
              onSubmit={handleEditGroup}
              onCancel={() => setIsEditModalOpen(false)}
              isLoading={isLoading}
            />
          </DialogContent>
        </Dialog>

        {/* Модальное окно добавления участника */}
        <AddMemberModal
          open={isAddMemberModalOpen}
          existingMemberIds={
            currentGroup.members
              ?.map((m) => m.student?.id)
              .filter((id): id is number => typeof id === 'number') || []
          }
          onSubmit={handleAddMemberSubmit}
          onCancel={() => setIsAddMemberModalOpen(false)}
        />
          </>
        )}
      </Box>
    </DashboardLayout>
  )
}
